import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';


import Header from '../../../components/ui/Header';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { foodService } from '../../../services/foodService';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams?.get('orderId');
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orderId) {
      navigate('/food');
      return;
    }

    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const [orderData, itemsData] = await Promise.all([
        foodService?.getOrderById(orderId),
        foodService?.getOrderItems(orderId)
      ]);
      setOrder(orderData);
      setOrderItems(itemsData);
    } catch (err) {
      setError('Errore nel caricamento dei dettagli ordine');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="flex items-center justify-center h-96">
          <Icon name="Loader2" size={48} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <div className="container mx-auto px-4 py-12 max-w-2xl text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Ordine non trovato</h1>
          <p className="text-muted-foreground mb-6">{error || 'Impossibile recuperare i dettagli dell\'ordine'}</p>
          <Button onClick={() => navigate('/food')}>Torna al Food</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Ordine Confermato - Cliccami Food</title>
      </Helmet>
      <Header onFilterClick={() => {}} />

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={48} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Ordine Confermato!</h1>
          <p className="text-lg text-muted-foreground">Il tuo ordine è stato ricevuto con successo</p>
        </div>

        {/* Order ID */}
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 mb-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">ID Ordine</p>
          <p className="text-xl font-mono font-bold text-primary">{order?.id}</p>
        </div>

        {/* Order Summary */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Package" size={24} />
            Riepilogo Ordine
          </h2>

          {/* Vendor Info */}
          <div className="mb-4 pb-4 border-b border-border">
            <p className="text-sm text-muted-foreground mb-1">Esercente</p>
            <p className="text-lg font-semibold text-foreground">{order?.vendor?.name}</p>
            <p className="text-sm text-muted-foreground">{order?.city?.name}</p>
          </div>

          {/* Products */}
          <div className="space-y-3 mb-4">
            {orderItems?.map(item => (
              <div key={item?.id} className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{item?.product_name}</p>
                  <p className="text-xs text-muted-foreground">Quantità: {item?.quantity}</p>
                </div>
                <p className="text-sm font-semibold text-foreground">€{item?.line_total?.toFixed(2)}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Totale Prodotti</span>
              <span className="text-foreground">€{order?.products_total?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Costo Consegna</span>
              <span className="text-foreground">€{order?.delivery_fee?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
              <span className="text-foreground">Totale</span>
              <span className="text-primary">€{order?.order_total?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Truck" size={24} />
            Dettagli Consegna
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Fascia Oraria Scelta</p>
              <p className="text-lg font-semibold text-primary">{order?.delivery_slot}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Indirizzo</p>
              <p className="text-sm text-foreground">{order?.delivery_address}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Contatto</p>
              <p className="text-sm text-foreground">{order?.customer_name}</p>
              <p className="text-sm text-foreground">{order?.customer_phone}</p>
            </div>
          </div>
        </div>

        {/* Important Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex gap-3">
            <Icon name="Info" size={24} className="text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Cosa succede ora?</h3>
              <p className="text-sm text-blue-800 leading-relaxed">
                <strong>Cliccami ha ricevuto il tuo ordine</strong> e ti contatterà per eventuali aggiornamenti sulla consegna. 
                Riceverai una notifica quando l'ordine sarà in preparazione.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="default"
            className="flex-1"
            onClick={() => navigate('/food')}
          >
            <Icon name="Home" size={20} className="mr-2" />
            Torna al Food
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => window.print()}
          >
            <Icon name="Printer" size={20} className="mr-2" />
            Stampa Riepilogo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;